/**
 * Página de Login
 * Tela de autenticação do usuário
 */

import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Divider,
  Avatar,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

/**
 * Componente de página de Login
 */
const Login = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuth();

  /**
   * Manipula o submit do formulário
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 [Login.js] handleSubmit chamado');
    console.log('📧 Email:', email);
    console.log('🔑 Senha:', senha ? '***' : 'vazia');
    
    setError('');
    setLoading(true);

    // Validação básica
    if (!email || !senha) {
      console.log('❌ [Login.js] Validação falhou');
      setError('Preencha todos os campos');
      setLoading(false);
      return;
    }

    console.log('✅ [Login.js] Validação OK, chamando login()...');
    
    // Tenta fazer login
    const result = await login(email, senha);

    console.log('📦 [Login.js] Resultado do login:', result);

    if (result.success) {
      const userData = JSON.parse(localStorage.getItem('user'));
      const perm = typeof userData?.permissoes === 'string'
        ? JSON.parse(userData.permissoes)
        : (userData?.permissoes || {});

      if (userData?.isAdmin) {
        navigate('/admin');
      } else if (userData?.role === 'secretaria') {
        if (perm.agenda) navigate('/agenda');
        else if (perm.pacientes) navigate('/pacientes');
        else navigate('/perfil');
      } else {
        navigate('/dashboard');
      }
    } else {
      console.log('❌ [Login.js] Login falhou:', result.message);
      setError(result.message);
    }

    setLoading(false);
  };

  // Tema claro fixo para a tela de login
  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      background: {
        default: '#f5f5f5',
        paper: '#fff',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
          },
        },
      },
    },
  });

  // Força o fundo do body com imagem e gradiente apenas nesta página
  React.useEffect(() => {
    const originalBg = document.body.style.background;
    document.body.style.background = `
      linear-gradient(135deg, rgba(25, 118, 210, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%),
      url('${process.env.PUBLIC_URL}/assets/fundo_login.jpg') center/cover no-repeat fixed
    `;
    return () => {
      document.body.style.background = originalBg;
    };
  }, []);

  return (
    <ThemeProvider theme={lightTheme}>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%', bgcolor: '#fff', boxShadow: 6, borderRadius: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={process.env.PUBLIC_URL + '/assets/gerencie_logo_nova.png'}
                alt="Logo Gerencie"
                style={{ maxWidth: 120, maxHeight: 120 }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
              <TextField
                variant="standard"
                required
                fullWidth
                id="email"
                label="E-mail"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{ disableUnderline: false }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, backgroundColor: 'transparent' }}
              />
              <TextField
                variant="standard"
                required
                fullWidth
                name="senha"
                label="Senha"
                type="password"
                id="senha"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                InputProps={{ disableUnderline: false }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, backgroundColor: 'transparent' }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2, borderRadius: 2, fontWeight: 600, fontSize: 16 }}
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                sx={{ mb: 2, borderRadius: 2, fontWeight: 600, fontSize: 16, textTransform: 'none', background: 'white', color: '#1976d2', borderColor: '#1976d2' }}
                startIcon={
                  <Avatar src={process.env.PUBLIC_URL + '/assets/Logo-Google-G.png'} alt="Google" sx={{ width: 22, height: 22, bgcolor: 'transparent' }} />
                }
                onClick={() => alert('Funcionalidade Google não implementada')}
              >
                Entrar com Google
              </Button>

              <Button
                fullWidth
                variant="text"
                sx={{ mb: 2, textTransform: 'none', color: '#1976d2', fontWeight: 500 }}
                component={RouterLink}
                to="/recuperar-senha"
              >
                Esqueci a senha
              </Button>

              <Divider sx={{ my: 2 }}>ou</Divider>

              <Button
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/cadastro"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, textTransform: 'none' }}
              >
                Criar nova conta
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Login;
